import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trackingApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Search, 
  Ship, 
  MapPin, 
  Calendar,
  Package,
  Clock,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { formatDateTime, getStatusColor } from '../../lib/utils';
import { EtaDisplay } from '../../components/shared/EtaDisplay';

const UserTracking = () => {
  const [searchParams] = useSearchParams();
  const [reference, setReference] = useState(searchParams.get('ref') || '');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!reference.trim()) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await trackingApi.track(reference);
      setTrackingData(response.data);
    } catch (err) {
      setError('Shipment not found');
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="user-tracking">
      <div>
        <h1 className="font-heading text-2xl md:text-[32px] sv-display">Track a shipment</h1>
        <p className="text-muted-foreground">Enter your tracking reference to monitor your cargo</p>
      </div>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleTrack} className="flex gap-3">
            <Input
              placeholder="Enter booking or container number..."
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="flex-1 sv-mono"
              data-testid="tracking-input"
            />
            <Button type="submit" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive">
          <CardContent className="p-6 text-center text-destructive">{error}</CardContent>
        </Card>
      )}

      {trackingData && (
        <div className="space-y-6" data-testid="tracking-result">
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <Ship className="h-8 w-8 text-secondary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Booking number</p>
                    <p className="font-heading text-xl font-medium tracking-[-0.01em] sv-mono">{trackingData.booking_number}</p>
                  </div>
                </div>
                <Badge className={`${getStatusColor(trackingData.status)} text-sm px-4 py-2`}>
                  {trackingData.status.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted rounded-sm">
                  <MapPin className="h-5 w-5 text-muted-foreground mb-2" />
                  <p className="text-xs text-muted-foreground">Origin</p>
                  <p className="font-medium">{trackingData.origin}</p>
                </div>
                <div className="p-4 bg-muted rounded-sm">
                  <MapPin className="h-5 w-5 text-muted-foreground mb-2" />
                  <p className="text-xs text-muted-foreground">Destination</p>
                  <p className="font-medium">{trackingData.destination}</p>
                </div>
                <div className="p-4 bg-muted rounded-sm">
                  <Package className="h-5 w-5 text-muted-foreground mb-2" />
                  <p className="text-xs text-muted-foreground">Current location</p>
                  <p className="font-medium">{trackingData.current_location}</p>
                </div>
                <div className="p-4 bg-muted rounded-sm">
                  <Calendar className="h-5 w-5 text-muted-foreground mb-2" />
                  <p className="text-xs text-muted-foreground">ETA</p>
                  <EtaDisplay booking={trackingData} className="font-medium" testId="user-tracking-eta" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Shipment timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {trackingData.events?.map((event, index) => (
                  <div key={index} className="flex gap-4 relative">
                    {index < trackingData.events.length - 1 && (
                      <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-muted"></div>
                    )}
                    <div className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      index === 0 ? 'bg-secondary text-white' : 'bg-muted'
                    }`}>
                      {index === 0 ? <Clock className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />}
                    </div>
                    <div className="flex-1 pb-4">
                      <p className="font-medium">{event.status}</p>
                      <p className="text-sm text-muted-foreground">{event.location}</p>
                      <p className="text-xs text-muted-foreground">{formatDateTime(event.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!trackingData && !error && (
        <Card>
          <CardContent className="py-16 text-center">
            <Ship className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-xl font-medium tracking-[-0.01em] mb-2">Track your cargo</h3>
            <p className="text-muted-foreground">
              Enter your booking number or container number above
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UserTracking;
